/**
 * Browser PostHog client: one lazy instance per page load, started only on a
 * page that may be captured (`isCapturablePage`). The SDK is a dynamic
 * import, so share viewers, token links, Previews and localhost never
 * download it on load.
 *
 * Cookieless is the resting state (`cookieless_mode: "on_reject"` with
 * opt-out as the default): no cookie, no stored id, no replay. Only a
 * signed-in layout's `AnalyticsIdentity` verdict opts a browser in.
 * - A page outside the signed-in apps starts at once, cookieless, whatever
 *   this browser did before. It is upgraded only if the person then signs
 *   in without a full page load (the operator login page does this).
 * - A page inside a signed-in app shell does not start on its own; it waits
 *   for the verdict:
 *   - a signed-in person: opt in, identify by internal ids, record replay;
 *   - signed out (a portal login page): cookieless;
 *   - impersonating: do not start, or stop sending if already started.
 *   No verdict (a failed lookup) means no capture on that load.
 *
 * The rules themselves live in `posthog-config.ts`.
 */

import type { PostHog } from "posthog-js";
import {
  POSTHOG_API_HOST,
  POSTHOG_CONSENT_STORAGE_KEY,
  POSTHOG_PROJECT_TOKEN,
  POSTHOG_UI_HOST,
  awaitsIdentity,
  isCapturablePage,
  isCredentialLikeInput,
  isLocalCaptureEnabled,
  sanitizeEvent,
} from "./posthog-config";

export type AnalyticsApp = "spacestation" | "spatialist" | "spacemanager";

export type AnalyticsIdentity = {
  /** `auth.users.id`: the PostHog distinct id. */
  userId: string;
  /** `virtualspace.people.id`, when the account is claimed. */
  personId: string | null;
  /** `basejump.accounts.id` of the acting organization, when there is one. */
  organizationId: string | null;
  app: AnalyticsApp;
};

/** Where the page load began: a public page, or inside a signed-in app. */
type Mode = "public" | "session";

/**
 * A public page keeps its capture choice under its own key and clears it
 * before starting, so a choice left by an earlier signed-in session can
 * never opt a public page in.
 */
const PUBLIC_CONSENT_STORAGE_KEY = `${POSTHOG_CONSENT_STORAGE_KEY}_page`;

let client: PostHog | null = null;
let starting: Promise<PostHog | null> | null = null;
/** True while a platform admin is viewing as or swapped into an account. */
let suppressed = false;
/** True while a client-side navigation sits on a page that is not captured. */
let offPage = false;
let recordingStopped = false;

/** Replay runs only opted in, on a captured page, unsuppressed. */
function syncRecording(): void {
  if (!client) return;
  const shouldStop = suppressed || offPage;
  if (shouldStop === recordingStopped) return;
  recordingStopped = shouldStop;
  if (shouldStop) client.stopSessionRecording();
  else client.startSessionRecording();
}

function setStoredCaptureChoice(key: string, optedIn: boolean): void {
  try {
    if (optedIn) window.localStorage.setItem(key, "1");
    else window.localStorage.removeItem(key);
  } catch {
    // Storage blocked: the SDK falls back to its default, cookieless.
  }
}

async function loadAndInit(startMode: Mode): Promise<PostHog> {
  const { default: posthog } = await import("posthog-js");
  posthog.init(POSTHOG_PROJECT_TOKEN, {
    api_host: POSTHOG_API_HOST,
    ui_host: POSTHOG_UI_HOST,
    defaults: "2026-05-30",
    // Cookieless until a layout opts the browser in; the SDK reads a stored
    // "1" under the consent key as that opt-in.
    cookieless_mode: "on_reject",
    opt_out_capturing_by_default: true,
    opt_out_capturing_persistence_type: "localStorage",
    consent_persistence_name:
      startMode === "public"
        ? PUBLIC_CONSENT_STORAGE_KEY
        : POSTHOG_CONSENT_STORAGE_KEY,
    // Both nest the page URL where the event gate cannot reach it cheaply:
    // heatmap data is keyed by full href, web vitals carry their own copy.
    capture_heatmaps: false,
    enable_heatmaps: false,
    capture_performance: { web_vitals: false },
    // Portal sessions are host-only; analytics identity follows them.
    cross_subdomain_cookie: false,
    person_profiles: "identified_only",
    capture_exceptions: true,
    disable_surveys: true,
    // Clicks are recorded by element, never by its text or link target:
    // both can be a person's name, an email or a token link.
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: {
      // The screen is recorded as shown. `maskAllInputs` routes every input
      // through `maskInputFn`, which hides only credential-like fields.
      maskAllInputs: true,
      maskInputFn: (text, element) =>
        element &&
        isCredentialLikeInput({
          type: element.getAttribute("type"),
          name: element.getAttribute("name"),
          id: element.id,
          autocomplete: element.getAttribute("autocomplete"),
        })
          ? "*".repeat(text.length)
          : text,
    },
    before_send: (event) =>
      sanitizeEvent(event, {
        suppressed,
        hostname: window.location.hostname,
        pathname: window.location.pathname,
      }),
  });
  if (isLocalCaptureEnabled()) posthog.register({ environment: "development" });
  client = posthog;
  syncRecording();
  return posthog;
}

/** Resolves to null where capture is off or the SDK could not load. */
function start(startMode: Mode): Promise<PostHog | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (client) return Promise.resolve(client);
  const { hostname, pathname } = window.location;
  if (!isCapturablePage(hostname, pathname)) return Promise.resolve(null);
  starting ??= loadAndInit(startMode).catch(() => {
    starting = null;
    return null;
  });
  return starting;
}

/**
 * Start cookieless capture for the current page, unless it sits inside a
 * signed-in app shell: there the layout's identity verdict starts the SDK.
 */
export function startAnalyticsForPage(): void {
  if (typeof window === "undefined") return;
  const { hostname, pathname } = window.location;
  if (awaitsIdentity(hostname, pathname)) return;
  if (!client && !starting) {
    setStoredCaptureChoice(PUBLIC_CONSENT_STORAGE_KEY, false);
  }
  void start("public");
}

const NAVIGATION_POLL_MS = 250;
const NAVIGATION_POLL_LIMIT = 40;
let navigationTimer: number | null = null;

/**
 * A client-side navigation is starting towards `url`. Replay pauses the
 * moment the destination is a page that is not captured. Everything that
 * turns capture back on waits until the address bar itself shows a captured
 * page: resuming replay, and starting the SDK on a load that began where
 * capture was off. Neither happens while the page being left is still up.
 */
export function noteAnalyticsNavigation(url: string): void {
  if (typeof window === "undefined") return;
  let destination: string;
  try {
    destination = new URL(url, window.location.href).pathname;
  } catch {
    return;
  }
  const { hostname } = window.location;
  if (navigationTimer !== null) window.clearInterval(navigationTimer);
  navigationTimer = null;
  if (!isCapturablePage(hostname, destination)) {
    offPage = true;
    syncRecording();
    return;
  }
  const settle = () => {
    if (!isCapturablePage(hostname, window.location.pathname)) return false;
    offPage = false;
    syncRecording();
    if (!client && !starting) startAnalyticsForPage();
    return true;
  };
  if (settle()) return;
  let polls = 0;
  navigationTimer = window.setInterval(() => {
    polls += 1;
    if (settle() || polls >= NAVIGATION_POLL_LIMIT) {
      if (navigationTimer !== null) window.clearInterval(navigationTimer);
      navigationTimer = null;
    }
  }, NAVIGATION_POLL_MS);
}

/** Opt this browser in and tie its events to the signed-in person. */
export async function identifyAnalyticsUser(
  identity: AnalyticsIdentity,
): Promise<void> {
  suppressed = false;
  // Before `init`, so the first page view is already sent opted in.
  if (!client && !starting) {
    setStoredCaptureChoice(POSTHOG_CONSENT_STORAGE_KEY, true);
  }
  const posthog = await start("session");
  if (!posthog || suppressed) return;
  // A different identified person on this browser: never merge the two.
  if (
    posthog.get_distinct_id() !== identity.userId &&
    posthog.get_property("$user_state") === "identified"
  ) {
    posthog.reset();
  }
  // After the reset, which also clears the stored choice.
  if (posthog.get_explicit_consent_status() !== "granted") {
    posthog.opt_in_capturing({ captureEventName: null });
  }
  if (posthog.get_distinct_id() !== identity.userId) {
    posthog.identify(
      identity.userId,
      identity.personId ? { person_id: identity.personId } : undefined,
    );
  }
  posthog.register({ app: identity.app });
  if (identity.organizationId) {
    posthog.group("organization", identity.organizationId);
  } else {
    posthog.resetGroups();
  }
  syncRecording();
}

/** A signed-out render inside a signed-in shell: cookieless. */
export async function clearAnalyticsUser(): Promise<void> {
  suppressed = false;
  if (!client && !starting) {
    setStoredCaptureChoice(POSTHOG_CONSENT_STORAGE_KEY, false);
  }
  const posthog = await start("session");
  if (!posthog || suppressed) return;
  if (posthog.get_explicit_consent_status() !== "granted") return;
  posthog.reset();
  posthog.clear_opt_in_out_capturing();
}

/**
 * Send nothing and record nothing until a person is identified again. Never
 * starts the SDK: an impersonated page load stays silent from the first byte.
 */
export function suppressAnalytics(): void {
  suppressed = true;
  syncRecording();
}

/**
 * Report an error a React boundary caught; a no-op where capture is off.
 *
 * A server-component error reaches the browser with its message stripped
 * and only `error.digest` (an opaque hash) left, so the digest is sent as a
 * property: it is the one key that matches the Vercel runtime log line.
 * Only a string digest is sent; nothing else is read off the error.
 */
export function captureAnalyticsException(
  error: unknown,
  properties?: Record<string, string | number | boolean>,
): void {
  if (!client) return;
  const digest =
    error && typeof error === "object" && "digest" in error
      ? (error as { digest?: unknown }).digest
      : undefined;
  const extra: Record<string, string | number | boolean> = { ...properties };
  if (typeof digest === "string" && digest.length > 0) extra.digest = digest;
  if (Object.keys(extra).length > 0) {
    client.captureException(error, extra);
  } else {
    client.captureException(error);
  }
}
